// EnrichmentBanner.jsx
// A fresh signup runs ~60s of background enrichment (Google Places + website
// scrape + OpenAI) before their business record is populated. Without feedback
// the new user just stares at empty fields. This banner polls
// GET /api/profile/enrichment-status and shows "setting up your profile…" while
// it's running, then refreshes the profile and disappears.
//
// Mounted once, app-wide. It renders NOTHING unless the logged-in user's
// business is actively enriching, so it's a no-op for everyone else (one cheap
// status check on login, then it stops). Fail-safe: any error hides it.
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useProfileStore } from '../../state/profileStore';

const POLL_MS = 5000;

export default function EnrichmentBanner() {
  const profile = useProfileStore((s) => s.profile);
  const refreshProfile = useProfileStore((s) => s.refreshProfile);
  const [running, setRunning] = useState(false);
  const wasRunning = useRef(false);

  const userId = profile?.u_id;

  useEffect(() => {
    if (!userId) { setRunning(false); wasRunning.current = false; return undefined; }

    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get('/api/profile/enrichment-status', {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        if (data?.status === 'running') {
          wasRunning.current = true;
          setRunning(true);
          timer = setTimeout(poll, POLL_MS);
        } else {
          // Done. If we'd been showing the banner, refresh so the freshly
          // enriched fields appear, then hide.
          if (wasRunning.current) {
            wasRunning.current = false;
            try { await refreshProfile?.(userId); } catch { /* non-fatal */ }
          }
          setRunning(false);
        }
      } catch {
        if (!cancelled) setRunning(false); // fail safe — never trap the UI
      }
    };

    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [userId, refreshProfile]);

  if (!running) return null;

  return (
    <div
      role="status"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        background: '#00aef7',
        color: '#fff',
        padding: '8px 16px',
        textAlign: 'center',
        fontSize: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
      }}
    >
      <span
        style={{
          width: 14,
          height: 14,
          border: '2px solid rgba(255,255,255,0.55)',
          borderTopColor: '#fff',
          borderRadius: '50%',
          display: 'inline-block',
          animation: 'eh-enrich-spin 0.8s linear infinite',
        }}
      />
      Setting up your profile — this usually takes about a minute. Feel free to look around.
      <style>{'@keyframes eh-enrich-spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}
